import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { MiDetailFields } from "@miplatform/components";
import {
  BUTTON_LABELS,
  INPUT_TYPES,
  PERMISSIONS,
  TIMEZONE_OPTIONS,
  states,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { ICityResponse, ITransferListItem } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useCities,
  useInspectionAuthorities,
  usePermissions,
} from "@mibuildweb/hooks";
import { TransferList } from "@mibuildweb/components";
import { Box, Typography } from "@mui/material";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";

const schema = z.object({
  cityName: z.string().min(1, "City Name is required"),
  countyName: z.string().min(1, "County Name is required"),
  regionCode: z.string().min(1, "State is required").refine(
    (v) => Object.keys(states).includes(v),
    "Invalid state code"
  ),
  countryCode: z.string().min(1, "Country Code is required"),
  timeZone: z.string().min(1, "Time Zone is required"),
});

type ICityFormData = z.infer<typeof schema>;

const defaultValues: ICityFormData = {
  cityName: "",
  countyName: "",
  regionCode: "",
  countryCode: "US",
  timeZone: "",
};

const timezoneSelectOptions = TIMEZONE_OPTIONS.map(
  ({ label, value }: { label: string; value: string }) => ({ label, value })
);

const stateSelectOptions = Object.entries(states).map(([code, name]) => ({
  label: name,
  value: code,
}));

export function ConfigCityDetails() {
  const { goBack, params } = useRouter();
  const { id } = params;

  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission(PERMISSIONS.CITY.UPDATE);

  const { createCity, updateCity, getCityById, isLoading: citiesLoading } =
    useCities();
  const {
    inspectionAuthorities,
    getInspectionAuthorities,
    isLoading: authoritiesLoading,
  } = useInspectionAuthorities();

  const [selectedAuthorities, setSelectedAuthorities] = useState<
    ITransferListItem[]
  >([]);
  const [authoritiesDirty, setAuthoritiesDirty] = useState(false);

  const {
    control,
    formState: { isValid, isDirty },
    getValues,
    reset,
    trigger,
  } = useForm<ICityFormData>({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(schema),
  });

  const isCreating = !id;
  const watchCityName = useWatch({ control, name: "cityName" });

  const isLoading = citiesLoading || authoritiesLoading;

  useEffect(() => {
    getInspectionAuthorities();
  }, []);

  useEffect(() => {
    if (!id) return;
    const loadCity = async () => {
      const response: { data?: ICityResponse } = await getCityById(id);
      if (response?.data) {
        reset({
          ...response.data,
          countryCode: response.data.countryCode ?? "US",
          timeZone: response.data.timeZone ?? "",
        });
        setSelectedAuthorities(
          (response.data.inspectionAuthorities ?? []).map((a) => ({
            key: a.id,
            title: a.name,
          }))
        );
        await trigger();
      }
    };
    loadCity().catch(() => goBack());
  }, [id]);

  const availableItems = useMemo<ITransferListItem[]>(
    () => inspectionAuthorities.map((a) => ({ key: a.id, title: a.name })),
    [inspectionAuthorities]
  );

  const handleAddItem = (key: string) => {
    const authority = inspectionAuthorities.find((a) => a.id === key);
    if (!authority) return;
    setSelectedAuthorities((prev) => [
      ...prev,
      { key: authority.id, title: authority.name },
    ]);
    setAuthoritiesDirty(true);
  };

  const handleRemoveItem = (key: string) => {
    setSelectedAuthorities((prev) => prev.filter((a) => a.key !== key));
    setAuthoritiesDirty(true);
  };

  const handleSave = async () => {
    const data = getValues();
    const inspectionAuthorityIds = selectedAuthorities.map((a) => a.key);
    if (id) {
      const response = await updateCity({
        id,
        data: { ...data } as ICityResponse,
        inspectionAuthorityIds,
      });
      if (response?.data) {
        reset({
          ...response.data,
          countryCode: response.data.countryCode ?? "US",
          timeZone: response.data.timeZone ?? "",
        });
        setAuthoritiesDirty(false);
      }
    } else {
      await createCity({
        data: { ...data, isValid: true, inspectionAuthorityIds },
      });
    }
  };

  const canSave = !isLoading && !isReadOnly && isValid && (isDirty || authoritiesDirty);

  const pageActions = isReadOnly
    ? null
    : {
        cancel: {
          label: BUTTON_LABELS.CANCEL,
          onClick: () => {
            reset();
            goBack();
          },
        },
        submit: {
          label: "Save",
          onClick: handleSave,
          disabled: !canSave,
        },
      };

  const renderPageTitle = () => {
    const action = isCreating ? "Create City" : "Edit City";
    const nameLabel = watchCityName || "City";
    return (
      <Typography variant="HD4">
        {action}:{" "}
        <Typography variant="HD4" color="primary" component="span">
          {nameLabel}
        </Typography>
      </Typography>
    );
  };

  const detailFields = useMemo(
    () => [
      {
        formControl: control,
        label: "State",
        name: "regionCode",
        width: "25%",
        required: true,
        fieldType: INPUT_TYPES.SELECT,
        options: stateSelectOptions,
        shrink: true,
      },
      {
        formControl: control,
        label: "City Name",
        name: "cityName",
        width: "25%",
        required: true,
      },
      {
        formControl: control,
        label: "County",
        name: "countyName",
        width: "25%",
        required: true,
      },
      {
        formControl: control,
        label: "Time Zone",
        name: "timeZone",
        width: "25%",
        required: true,
        fieldType: INPUT_TYPES.SELECT,
        options: timezoneSelectOptions,
        shrink: true,
      },
    ],
    [control]
  );

  return (
    <ConfigPageLayout
      renderTitle={renderPageTitle}
      bottomActions={pageActions}
      loading={isLoading}
    >
      <Box display="flex" flexDirection="column" gap={2} p={4} height="100%">
        <ConfigAccordionHeader title="City Details">
          <MiDetailFields detailFields={detailFields} />
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Inspection Authorities">
          <TransferList
            showAvailableItemsSearch
            availableItems={availableItems}
            selectedItems={selectedAuthorities}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
            availableTitle="Available Inspection Authorities"
            selectedTitle="Selected Inspection Authorities"
            emptyAvailableListMessage="No inspection authorities available."
            emptySelectedListMessage="No Inspection Authorities Selected"
            disabled={isReadOnly}
          />
        </ConfigAccordionHeader>
      </Box>
    </ConfigPageLayout>
  );
}